import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { trainingMaterialsApi, uploadFile } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { 
  BookOpen, Plus, Video, FileText, Presentation, Download, 
  Upload, Trash2, Eye, Play, File, ExternalLink, Link2 
} from 'lucide-react';

const CATEGORIES = [
  { value: 'slide', label: 'Slide/Presentazione', icon: Presentation },
  { value: 'video', label: 'Video', icon: Video },
  { value: 'documento', label: 'Documento', icon: FileText },
  { value: 'esercizio', label: 'Esercizio/Quiz', icon: BookOpen },
  { value: 'test_esterno', label: 'Test Esterno (Google Forms)', icon: ExternalLink },
];

const COURSE_TYPES = [
  { value: 'sicurezza_generale', label: 'Sicurezza Generale' },
  { value: 'sicurezza_specifica', label: 'Sicurezza Specifica' },
  { value: 'antincendio', label: 'Antincendio' },
  { value: 'primo_soccorso', label: 'Primo Soccorso' },
  { value: 'rspp', label: 'RSPP/ASPP' },
  { value: 'preposti', label: 'Preposti' },
  { value: 'rls', label: 'RLS' },
  { value: 'altro', label: 'Altro' },
];

export default function TrainingMaterialsPage() {
  const { user } = useAuth();
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterCourse, setFilterCourse] = useState('all');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'documento',
    file_url: '',
    file_type: '',
    course_type: '',
    is_public: false,
    order_index: 0,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await trainingMaterialsApi.getAll();
      setMaterials(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'documento',
      file_url: '',
      file_type: '',
      course_type: '',
      is_public: false,
      order_index: 0,
    });
    setSelectedMaterial(null);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const folder = formData.category === 'video' ? 'videos' : 'training_materials';
      const response = await uploadFile(folder, file);
      setFormData({
        ...formData,
        file_url: response.data.url,
        file_type: file.type,
      });
      toast.success('File caricato');
    } catch (error) {
      toast.error('Errore upload');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedMaterial) {
        await trainingMaterialsApi.update(selectedMaterial.id, formData);
        toast.success('Materiale aggiornato');
      } else {
        await trainingMaterialsApi.create(formData);
        toast.success('Materiale aggiunto');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleEdit = (material) => {
    setSelectedMaterial(material);
    setFormData({
      title: material.title,
      description: material.description || '',
      category: material.category,
      file_url: material.file_url,
      file_type: material.file_type || '',
      course_type: material.course_type || '',
      is_public: material.is_public,
      order_index: material.order_index || 0,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo materiale?')) return;
    try {
      await trainingMaterialsApi.delete(id);
      toast.success('Materiale eliminato');
      loadData();
    } catch (error) {
      toast.error('Errore');
    }
  };

  const handleDownload = async (material) => {
    try {
      await trainingMaterialsApi.trackDownload(material.id);
      // For external links (Google Forms, etc.), open directly
      if (material.file_type === 'external_link' || material.category === 'test_esterno') {
        window.open(material.file_url, '_blank');
      } else {
        window.open(material.file_url, '_blank');
      }
    } catch (error) {
      // For external links, open directly
      if (material.file_type === 'external_link' || material.category === 'test_esterno') {
        window.open(material.file_url, '_blank');
      } else {
        window.open(material.file_url, '_blank');
      }
    }
  };

  const getCategoryIcon = (category) => {
    const cat = CATEGORIES.find(c => c.value === category);
    return cat ? cat.icon : File;
  };

  const filteredMaterials = materials.filter(m => {
    if (filterCategory !== 'all' && m.category !== filterCategory) return false;
    if (filterCourse !== 'all' && m.course_type !== filterCourse) return false;
    return true;
  });

  const canManage = ['super_admin', 'admin', 'amministratore', 'rspp', 'docente', 'docente_online', 'formatore', 'teacher'].includes(user?.role);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="training-materials-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-3xl">Materiale Didattico</h1>
          <p className="text-muted-foreground mt-1">Slide, video e documenti per la formazione</p>
        </div>
        {canManage && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-material-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuovo Materiale
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{selectedMaterial ? 'Modifica Materiale' : 'Nuovo Materiale Didattico'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Titolo *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="Titolo del materiale"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Categoria</Label>
                    <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(c => (
                          <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo Corso</Label>
                    <Select value={formData.course_type || "none"} onValueChange={(v) => setFormData({...formData, course_type: v === "none" ? "" : v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleziona..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Nessuno</SelectItem>
                        {COURSE_TYPES.map(c => (
                          <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Descrizione</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Descrizione del materiale..."
                  />
                </div>

                <div className="space-y-2">
                  <Label>{formData.category === 'test_esterno' ? 'Link Test *' : 'File *'}</Label>
                  {formData.category === 'test_esterno' ? (
                    <div className="space-y-2">
                      <Input
                        value={formData.file_url}
                        onChange={(e) => setFormData({...formData, file_url: e.target.value, file_type: 'external_link'})}
                        placeholder="https://docs.google.com/forms/d/..."
                        required
                      />
                      <p className="text-xs text-muted-foreground">
                        Inserisci il link al test esterno (Google Forms, Microsoft Forms, Typeform, ecc.)
                      </p>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        value={formData.file_url}
                        onChange={(e) => setFormData({...formData, file_url: e.target.value})}
                        placeholder="URL del file o carica..."
                        className="flex-1"
                      />
                      <input
                        ref={fileInputRef}
                        type="file"
                        onChange={handleFileUpload}
                        className="hidden"
                        accept={formData.category === 'video' ? 'video/*' : '*'}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {uploading ? 'Caricamento...' : 'Carica'}
                      </Button>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="is_public"
                    checked={formData.is_public}
                    onCheckedChange={(checked) => setFormData({...formData, is_public: checked})}
                  />
                  <Label htmlFor="is_public" className="cursor-pointer">
                    Visibile a tutti gli utenti
                  </Label>
                </div>

                <Button type="submit" className="w-full" disabled={!formData.title || !formData.file_url}>
                  {selectedMaterial ? 'Salva Modifiche' : 'Aggiungi Materiale'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {CATEGORIES.map(cat => {
          const count = materials.filter(m => m.category === cat.value).length;
          const Icon = cat.icon;
          return (
            <Card key={cat.value}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{count}</p>
                    <p className="text-sm text-muted-foreground">{cat.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Categoria" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le categorie</SelectItem>
            {CATEGORIES.map(c => (
              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterCourse} onValueChange={setFilterCourse}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Tipo corso" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i corsi</SelectItem>
            {COURSE_TYPES.map(c => (
              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Materials Grid */}
      {filteredMaterials.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Nessun materiale didattico disponibile</p>
            {canManage && (
              <Button className="mt-4" onClick={() => setDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Aggiungi il primo
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredMaterials.map((material) => {
            const Icon = getCategoryIcon(material.category);
            return (
              <motion.div
                key={material.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className={`p-3 rounded-lg ${
                        material.category === 'video' ? 'bg-red-100' :
                        material.category === 'slide' ? 'bg-blue-100' :
                        material.category === 'esercizio' ? 'bg-green-100' : 'bg-gray-100'
                      }`}>
                        <Icon className={`w-6 h-6 ${
                          material.category === 'video' ? 'text-red-600' :
                          material.category === 'slide' ? 'text-blue-600' :
                          material.category === 'esercizio' ? 'text-green-600' : 'text-gray-600'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium truncate">{material.title}</h3>
                        {material.course_type && (
                          <Badge variant="outline" className="mt-1">
                            {COURSE_TYPES.find(c => c.value === material.course_type)?.label}
                          </Badge>
                        )}
                        {material.description && (
                          <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                            {material.description}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Download className="w-4 h-4" />
                        <span>{material.download_count || 0} download</span>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => handleDownload(material)}>
                          {material.category === 'video' ? <Play className="w-4 h-4" /> : 
                           material.category === 'test_esterno' ? <ExternalLink className="w-4 h-4" /> :
                           <Download className="w-4 h-4" />}
                        </Button>
                        {canManage && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => handleEdit(material)}>
                              Modifica
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => handleDelete(material.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
